import React from "react";
import { Info, AlertTriangle, AlertCircle, CheckCircle2, X } from "../../icons";

/**
 * Toast + ToastViewport — transient notifications (Phase 2d Rich).
 *
 * Brief §9.8: bottom-right corner, max 3 visible, newest at the bottom,
 * 8px vertical gap, auto-dismiss 5s for info/success and 8s for
 * warn/danger, dismissible via X (most-recent first via Escape).
 *
 * API:
 *
 *   // Mount once near the app root (App.tsx).
 *   <ToastViewport />
 *
 *   // Anywhere in the app — fire-and-forget. No hook needed.
 *   import { toast } from "@/components/ui/Toast";
 *   toast.success({ title: "Saved", message: "All AE numbers persisted." });
 *   toast.info("Cron paused — refresh manually.");
 *   toast.warn("Some cells use cached data.");
 *   toast.danger({ title: "Save failed", message: "HubSpot returned 503." });
 *
 *   // Dismiss the most recent (or by id).
 *   toast.dismiss();
 *   toast.dismiss(id);
 *
 *   // Empty the stack.
 *   toast.clear();
 *
 * Phase 3 migrates window.toast consumers (state-storage.ts, hooks.ts,
 * etc.) onto this. For now this primitive coexists with window.toast.
 */

export type ToastTone = "info" | "success" | "warn" | "danger";

export interface ToastInput {
  title?: React.ReactNode;
  message?: React.ReactNode;
  /** Override auto-dismiss ms. 0 = manual dismiss only. */
  duration?: number;
  /** Optional explicit id. Useful for deduping / programmatic dismiss. */
  id?: string;
}

interface ToastRecord {
  id: string;
  tone: ToastTone;
  title?: React.ReactNode;
  message?: React.ReactNode;
  /** Wall-clock ms to auto-dismiss at. */
  expiresAt?: number;
}

const DEFAULT_DURATION_MS: Record<ToastTone, number> = {
  info:    5000,
  success: 5000,
  warn:    8000,
  danger:  8000,
};

const MAX_VISIBLE = 3;

// ─── Event bus ────────────────────────────────────────────────────────────
// Module-scoped subscriber pattern. ToastViewport subscribes on mount and
// owns the in-memory stack. Multiple viewports are not supported — second
// instance just gets the same events; behavior undefined.

type Listener = (records: ToastRecord[]) => void;
let stack: ToastRecord[] = [];
const listeners = new Set<Listener>();
let seq = 0;
function uid() { return `t_${Date.now().toString(36)}_${(seq++).toString(36)}`; }

function broadcast() {
  for (const fn of listeners) fn(stack);
}
function add(tone: ToastTone, input: ToastInput | string) {
  const opts: ToastInput = typeof input === "string" ? { message: input } : input;
  const id = opts.id || uid();
  const duration = opts.duration ?? DEFAULT_DURATION_MS[tone];
  const rec: ToastRecord = {
    id,
    tone,
    title: opts.title,
    message: opts.message,
    expiresAt: duration > 0 ? Date.now() + duration : undefined,
  };
  // Dedupe by id (overwrite earlier in place).
  stack = [...stack.filter(t => t.id !== id), rec];
  broadcast();
  return id;
}
function remove(id?: string) {
  if (!id) {
    stack = stack.slice(0, -1);
  } else {
    stack = stack.filter(t => t.id !== id);
  }
  broadcast();
}
function clear() {
  stack = [];
  broadcast();
}

export const toast = {
  info:    (input: ToastInput | string) => add("info",    input),
  success: (input: ToastInput | string) => add("success", input),
  warn:    (input: ToastInput | string) => add("warn",    input),
  danger:  (input: ToastInput | string) => add("danger",  input),
  dismiss: (id?: string) => remove(id),
  clear,
};
// DEV diagnostic — expose internals on window so the Lab + tests can
// poke at the stack. Tree-shaken in production builds.
if (typeof window !== "undefined" && import.meta.env?.DEV) {
  (window as any).__TOAST__ = {
    get stack() { return [...stack]; },
    get listeners() { return listeners.size; },
    toast,
  };
}

// ─── Viewport ──────────────────────────────────────────────────────────────

function iconFor(tone: ToastTone) {
  switch (tone) {
    case "info":    return <Info          size={16} />;
    case "success": return <CheckCircle2  size={16} />;
    case "warn":    return <AlertTriangle size={16} />;
    case "danger":  return <AlertCircle   size={16} />;
  }
}

export function ToastViewport() {
  const [records, setRecords] = React.useState<ToastRecord[]>(stack);

  // Subscribe on mount.
  React.useEffect(() => {
    const fn: Listener = (next) => setRecords([...next]);
    listeners.add(fn);
    setRecords([...stack]); // initial flush
    return () => { listeners.delete(fn); };
  }, []);

  // Auto-dismiss scheduling. Whenever the record list changes, set timers
  // for each expiresAt; clear on unmount/re-run.
  React.useEffect(() => {
    if (records.length === 0) return;
    const timers: ReturnType<typeof setTimeout>[] = [];
    const now = Date.now();
    for (const r of records) {
      if (r.expiresAt == null) continue;
      const ms = Math.max(0, r.expiresAt - now);
      timers.push(setTimeout(() => remove(r.id), ms));
    }
    return () => { for (const t of timers) clearTimeout(t); };
  }, [records]);

  // Escape dismisses the most recent.
  React.useEffect(() => {
    if (records.length === 0) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") remove();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [records]);

  if (records.length === 0) return null;
  // Newest at the BOTTOM — keep the natural source order (newest pushed
  // last). Cap to MAX_VISIBLE; older toasts are dropped from the visible
  // stack but stay in memory (so the timer still fires and the underlying
  // stack continues to drain).
  const visible = records.slice(-MAX_VISIBLE);

  return (
    <div className="v2-toast-viewport" role="region" aria-label="Notifications">
      {visible.map(r => (
        <div
          key={r.id}
          className={`v2-toast v2-toast--${r.tone}`}
          role={r.tone === "danger" || r.tone === "warn" ? "alert" : "status"}
        >
          <span className="v2-toast__icon" aria-hidden="true">{iconFor(r.tone)}</span>
          <div className="v2-toast__body">
            {r.title ? <div className="v2-toast__title">{r.title}</div> : null}
            {r.message ? <div className="v2-toast__message">{r.message}</div> : null}
          </div>
          <button
            type="button"
            className="v2-toast__dismiss"
            onClick={() => remove(r.id)}
            aria-label="Dismiss"
          >
            <X size={14} />
          </button>
        </div>
      ))}
    </div>
  );
}

export default ToastViewport;
